import {
  DIRECTIONS,
  DIRECTION_ELEMENTS,
  ROOMS,
  VASTU_CONVENTION,
  VERDICT_LABELS,
  type Direction,
  type Room,
  lookup,
} from '../../astro/tables/vastu.ts';
import { reportToolComplete, reportToolCta, reportToolStart } from './analytics.ts';

/**
 * Vastu Direction Checker — the one tool that needs no astronomy.
 *
 * Everything is a table lookup, so there is no uncertainty to gate. What the
 * output does carry is the *reasoning* behind each verdict, not just the
 * verdict: "the South-East is the quarter of Agni" is more useful, and more
 * honest, than a bare red label.
 */

const REVEAL_DELAY_MS = 500;

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text) node.textContent = text;
  return node;
}

/** 120px compass with the chosen direction highlighted. */
function compassSvg(direction: Direction): SVGSVGElement {
  const NS = 'http://www.w3.org/2000/svg';
  const svg = document.createElementNS(NS, 'svg');
  svg.setAttribute('viewBox', '0 0 120 120');
  svg.setAttribute('class', 'compass-svg');
  svg.setAttribute('role', 'img');
  svg.setAttribute('aria-label', `Compass showing ${direction}`);

  const ring = document.createElementNS(NS, 'circle');
  ring.setAttribute('cx', '60');
  ring.setAttribute('cy', '60');
  ring.setAttribute('r', '52');
  ring.setAttribute('class', 'compass-svg__ring');
  svg.appendChild(ring);

  DIRECTIONS.forEach((dir, i) => {
    // Index 0 is North, at the top; each step is 45 degrees clockwise.
    const angle = (i * 45 - 90) * (Math.PI / 180);
    const active = dir === direction;

    const tick = document.createElementNS(NS, 'line');
    tick.setAttribute('x1', String(60 + Math.cos(angle) * 40));
    tick.setAttribute('y1', String(60 + Math.sin(angle) * 40));
    tick.setAttribute('x2', String(60 + Math.cos(angle) * 52));
    tick.setAttribute('y2', String(60 + Math.sin(angle) * 52));
    tick.setAttribute('class', `compass-svg__tick${active ? ' is-active' : ''}`);
    svg.appendChild(tick);

    const label = document.createElementNS(NS, 'text');
    label.setAttribute('x', String(60 + Math.cos(angle) * 30));
    label.setAttribute('y', String(60 + Math.sin(angle) * 30 + 3.5));
    label.setAttribute('class', `compass-svg__label${active ? ' is-active' : ''}`);
    label.textContent = dir === 'North' ? 'N' : dir === 'South' ? 'S'
      : dir === 'East' ? 'E' : dir === 'West' ? 'W'
      : dir.split('-').map((w) => w[0]).join('');
    svg.appendChild(label);
  });

  // Needle pointing at the chosen direction.
  const idx = DIRECTIONS.indexOf(direction);
  const angle = (idx * 45 - 90) * (Math.PI / 180);
  const needle = document.createElementNS(NS, 'line');
  needle.setAttribute('x1', '60');
  needle.setAttribute('y1', '60');
  needle.setAttribute('x2', String(60 + Math.cos(angle) * 36));
  needle.setAttribute('y2', String(60 + Math.sin(angle) * 36));
  needle.setAttribute('class', 'compass-svg__needle');
  svg.appendChild(needle);

  const hub = document.createElementNS(NS, 'circle');
  hub.setAttribute('cx', '60');
  hub.setAttribute('cy', '60');
  hub.setAttribute('r', '3.5');
  hub.setAttribute('class', 'compass-svg__hub');
  svg.appendChild(hub);

  return svg;
}

function renderResult(container: HTMLElement, room: Room, direction: Direction): void {
  const cell = lookup(room, direction);
  const { element, lord } = DIRECTION_ELEMENTS[direction];

  container.replaceChildren();

  const head = el('div', 'tool-result__head');
  head.appendChild(compassSvg(direction));

  const summary = el('div', 'tool-result__summary');
  summary.appendChild(el('p', 'tool-result__where', `${room} in the ${direction}`));

  const verdict = el('p', `tool-result__verdict is-${cell.verdict}`, VERDICT_LABELS[cell.verdict]);
  summary.appendChild(verdict);

  summary.appendChild(
    el('p', 'tool-result__element', `${element} · presiding lord ${lord}`),
  );
  head.appendChild(summary);
  container.appendChild(head);

  container.appendChild(el('p', 'tool-result__principle', cell.principle));

  container.appendChild(el('h4', 'tool-result__subhead', 'What to do'));
  const list = el('ul', 'tool-result__remedies');
  for (const remedy of cell.remedies) list.appendChild(el('li', undefined, remedy));
  container.appendChild(list);

  const method = el('details', 'tool-method');
  const methodSummary = el('summary', undefined, 'How this is worked out');
  method.appendChild(methodSummary);
  method.appendChild(el('p', undefined, VASTU_CONVENTION));
  container.appendChild(method);

  /* Verdict-aware bridge (REDESIGN_PLAN.md §5, D-21). The promise is
     relevance, not gating: we sell only when the tool genuinely can't go
     further, and a good placement gets congratulated, not upsold. The
     booking URL comes from the page (data-book-url), never hardcoded. */
  const bookUrl = container.getAttribute('data-book-url');
  const bridge = el('div', 'tool-bridge');
  if (cell.verdict === 'needs-remedy' && bookUrl) {
    bridge.appendChild(
      el('p', 'tool-bridge__text',
        'These are the general remedies for this direction. A consultation maps your actual floor plan’s degrees — the tool can’t see your home.'),
    );
    const cta = el('a', 'btn btn-primary', 'Book a free 30-min call');
    cta.setAttribute('href', bookUrl);
    cta.setAttribute('target', '_blank');
    cta.setAttribute('rel', 'noopener');
    cta.addEventListener('click', () => reportToolCta('vastu-direction', cell.verdict));
    bridge.appendChild(cta);
  } else {
    bridge.appendChild(
      el('p', 'tool-bridge__text',
        cell.verdict === 'auspicious'
          ? 'Good placement — nothing to fix here. Check another room, or take the checklist with you.'
          : 'Workable placement — the remedies above are enough. Check another room, or take the checklist with you.'),
    );
    const dl = el('a', 'tool-bridge__link', 'Free Vastu checklist (PDF)');
    dl.setAttribute('href', '/downloads/vastu-flat-checklist.pdf');
    bridge.appendChild(dl);
  }
  container.appendChild(bridge);

  container.hidden = false;
  // Force a frame so the reveal transition actually runs.
  requestAnimationFrame(() => container.classList.add('is-revealed'));

  // A verdict is on screen — this is the tool's only success point.
  reportToolComplete('vastu-direction');
}

export function initVastuTool(): void {
  const form = document.getElementById('vastu-form') as HTMLFormElement | null;
  if (!form) return;

  const roomSelect = form.querySelector<HTMLSelectElement>('#vastu-room');
  const dirSelect = form.querySelector<HTMLSelectElement>('#vastu-direction');
  const output = document.getElementById('vastu-output');
  const skeleton = document.getElementById('vastu-skeleton');
  if (!roomSelect || !dirSelect || !output || !skeleton) return;

  // Populate from the table itself, so the options can never drift from it.
  for (const room of ROOMS) roomSelect.appendChild(new Option(room, room));
  for (const dir of DIRECTIONS) dirSelect.appendChild(new Option(dir, dir));

  let started = false;
  form.addEventListener('submit', (e) => {
    e.preventDefault();
    const room = roomSelect.value as Room;
    const direction = dirSelect.value as Direction;
    if (!room || !direction) return;
    if (!started) {
      started = true;
      reportToolStart('vastu-direction');
    }

    output.hidden = true;
    output.classList.remove('is-revealed');
    skeleton.hidden = false;

    // A brief pause so the result reads as considered rather than flickering
    // into place; the lookup itself is instant.
    window.setTimeout(() => {
      skeleton.hidden = true;
      renderResult(output, room, direction);
      output.setAttribute('tabindex', '-1');
      output.focus({ preventScroll: true });
    }, REVEAL_DELAY_MS);
  });
}
